import React, { useEffect, useRef, useState } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat } from "ol/proj";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/MapPage.css";
import DynamicCard from "../components/DynamicCard";
import PublicHeader from "../components/PublicHeader";
import SiteMenu from "../components/SiteMenu";
import { FaDownload, FaChevronRight, FaFish } from "react-icons/fa";
import { GiWheat, GiBirdTwitter, GiHand, GiEarthAmerica } from "react-icons/gi";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { IconType } from "react-icons";
import { useSearchParams } from "react-router-dom";
import { Rapport } from "../types/Rapport";
import { Domaine } from "../types/Domaine";
import { useAuth } from "../context/AuthContext";
import { ileService } from "../services/IleService";
import { regionService } from "../services/RegionService";
import { communeService } from "../services/CommuneService";
import { localiteService } from "../services/LocaliteService";
import { Ile } from "../types/Ile";
import { Region } from "../types/Region";
import { Commune } from "../types/Commune";
import { Localite } from "../types/Localite";
import CollecteErrorMessage from "../components/CollecteErrorMessage";
import { rapportGenereService } from "../services/RapportGenereService";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const SafeIcon: React.FC<{ icon?: IconType; size?: number; className?: string }> = ({ icon, size, className }) => {
  if (!icon) return null;
  const IconComp = icon as React.ElementType;
  return <IconComp size={size} className={className} />;
};

interface Props {
  useStaticData?: boolean;
}

const MapPage: React.FC<Props> = ({ useStaticData = true }) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const infoBoxRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();
  const subParam = searchParams.get("sub") || "";
  const [selectedSubDomain, setSelectedSubDomain] = useState(subParam);
  const [infoBoxRetracted, setInfoBoxRetracted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);

  const [iles, setIles] = useState<Ile[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [communes, setCommunes] = useState<Commune[]>([]);
  const [localites, setLocalites] = useState<Localite[]>([]);

  const [showPopup, setShowPopup] = useState(false);
  const [selectedIle, setSelectedIle] = useState<number | "">("");
  const [selectedRegion, setSelectedRegion] = useState<number | "">("");
  const [selectedCommune, setSelectedCommune] = useState<number | "">("");
  const [selectedLocalite, setSelectedLocalite] = useState<number | "">("");

  const [selectedIdFenetre, setSelectedIdFenetre] = useState<number | null>(null);
  const [selectedIdSousFenetre, setSelectedIdSousFenetre] = useState<number | null>(null);

  const [rapports, setRapports] = useState<Rapport[]>([]);
  const [rapport, setRapport] = useState<Rapport | null>(null);
  const [dynamicGraphData, setDynamicGraphData] = useState<any[]>([]);
  const [allDataSets, setAllDataSets] = useState<any[][]>([]);
  const [currentDataSetIndex, setCurrentDataSetIndex] = useState(0);
  const [hasClickedValidate, setHasClickedValidate] = useState(false);
  const { api } = useAuth();

  const exportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const fetchLocalities = async () => {
      try {
        const iles = await ileService.getAll(api);
        const regions = await regionService.getAllRegions(api);
        const communes = await communeService.getAll(api);
        const localites = await localiteService.getAll(api);

        setIles(iles);
        setRegions(regions);
        setCommunes(communes);
        setLocalites(localites);
      } catch (error) {
        console.error("Erreur lors de la récupération des données géographiques :", error);
      }
    };

    fetchLocalities();
  }, [api]);

  const [domaines] = useState<Domaine[]>([
    { label: "Agriculture", icon: GiWheat, sousDomaines: [{ label: "Structure et Démographie des exploitants" }, { label: "Foncier et Ressources" }, { label: "Contraintes Agricoles" }] },
    { label: "Élevage", icon: GiBirdTwitter, sousDomaines: [{ label: "Effectifs du bétail" }, { label: "Types d'élevage" }, { label: "Production animale" }] },
    { label: "Pêche & Aquaculture", icon: FaFish, sousDomaines: [{ label: "Techniques de pêche" }, { label: "Zones de pêche" }, { label: "Production halieutique" }] },
    { label: "Artisanat", icon: GiHand, sousDomaines: [{ label: "Identification des artisans" }, { label: "Activités artisanales" }, { label: "Production artisanale" }] },
    { label: "Changement Climatique", icon: GiEarthAmerica, sousDomaines: [{ label: "Vulnérabilité climatique" }, { label: "Émissions CO₂" }, { label: "Mesures d'adaptation" }] }
  ]);

  const activities: { label: string; path: string }[] = [
    { label: "Appel d'offre", path: "/offre" },
    { label: "Projet", path: "/project" },
    { label: "Calendrier", path: "/calendar" },
    { label: "Événements", path: "/event" }
  ];

  const extractNumericValue = (item: any): number => {
    for (const key of Object.keys(item)) {
      const value = item[key];
      if (typeof value === "number") return value;
      if (typeof value === "string" && !isNaN(Number(value))) {
        return Number(value);
      }
    }
    return 0;
  };

  const getNumericColumnName = (item: any): string => {
    for (const key of Object.keys(item)) {
      const value = item[key];
      if (
        typeof value === "number" ||
        (typeof value === "string" && !isNaN(Number(value)))
      ) {
        return key;
      }
    }
    return "";
  };

  const getLabelValue = (item: any): string | null => {
    for (const key of Object.keys(item)) {
      const value = item[key];

      if (
        typeof value === "string" &&
        isNaN(Number(value)) &&
        value.trim() !== ""
      ) {
        return value;
      }
    }
    return null;
  };

  const buildLabel = (item: any): string => {
    const parts: string[] = [];

    const numericValue = extractNumericValue(item);
    const numericColumn = getNumericColumnName(item);
    const labelValue = getLabelValue(item);

    parts.push(String(numericValue));

    if (numericColumn) parts.push(numericColumn);
    if (labelValue) parts.push(labelValue);

    return parts.join(" - ");
  };

  useEffect(() => {
    if (!useStaticData) {
      const saved = localStorage.getItem("rapportsGeneres");
      if (!saved) return;
      const parsed: Rapport[] = JSON.parse(saved);
      setRapports(parsed);
      setRapport(parsed[0] ?? null);
    }
  }, [useStaticData]);

  useEffect(() => {
    if (allDataSets.length > 0 && currentDataSetIndex < allDataSets.length) {
      setDynamicGraphData(allDataSets[currentDataSetIndex]);
    }
  }, [currentDataSetIndex, allDataSets]);

  const updateRapport = (subDomain?: string) => {
    if (!subDomain || useStaticData) return;
    const filtered = rapports.filter(r => r.titre === subDomain);
    setRapport(filtered[0] ?? null);
  };

  useEffect(() => {
    if (!mapRef.current) return;
    const map = new Map({
      target: mapRef.current,
      layers: [new TileLayer({ source: new OSM() })],
      view: new View({ center: fromLonLat([44.70, -12]), zoom: 8.7 }),
    });
    return () => map.setTarget(undefined);
  }, []);

  // Fonction d'export PDF
  const exportToPDF = async () => {
    if (!exportRef.current) return;

    setIsExporting(true);

    const element = exportRef.current;

    const originalWidth = element.style.width;
    const originalMaxWidth = element.style.maxWidth;

    try {
      element.style.width = "794px";
      element.style.maxWidth = "794px";

      await new Promise(r => setTimeout(r, 50));

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        width: element.offsetWidth,
        height: element.scrollHeight
      });

      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        10,
        10,
        imgWidth,
        imgHeight
      );

      pdf.save("rapport_stats.pdf");
    } catch (err) {
      console.error("Erreur export PDF :", err);
    } finally {
      element.style.width = originalWidth;
      element.style.maxWidth = originalMaxWidth;

      setIsExporting(false);
    }
  };

  let statsData: any[] = [];
  let pieData: any[] = [];
  let graphData: any[] = [];

  if (dynamicGraphData.length > 0) {
    statsData = dynamicGraphData.map((item, index) => {
      const numValue = extractNumericValue(item);
      return {
        label: buildLabel(item),
        value: numValue.toFixed(2),
        color: index % 4 === 0 ? "#198754" : index % 4 === 1 ? "#dc3545" : index % 4 === 2 ? "#ffc107" : "#0d6efd"
      };
    });

    pieData = dynamicGraphData.map(item => ({
      label: buildLabel(item),
      value: extractNumericValue(item)
    }));

    graphData = dynamicGraphData.map(item => {
      const value = extractNumericValue(item);
      return {
        name: buildLabel(item),
        value1: value,
        value2: value * 1.2
      };
    });
  } else if (hasClickedValidate) {
    statsData = [{ label: "Aucune donnée disponible", value: "-", color: "#6c757d" }];
    pieData = [{ label: "Aucune donnée disponible", value: 0 }];
    graphData = [{ name: "Aucune donnée", value1: 0, value2: 0 }];
  } else {
    statsData = [{ label: "Veuillez sélectionner un domaine et une localisation", value: "", color: "#6c757d" }];
    pieData = [{ label: "En attente de sélection", value: 0 }];
    graphData = [{ name: "En attente", value1: 0, value2: 0 }];
  }

  return (
    <>
      <div id="map-container" ref={mapRef} />

      <div className="map-page-header">
        <PublicHeader />
      </div>

      <SiteMenu
        activities={activities}
        onSelectSubDomain={(sdLabel, idFenetre, idSousFenetre) => {
          setSelectedSubDomain(sdLabel);
          updateRapport(sdLabel);
          setSelectedIdFenetre(idFenetre ?? null);
          setSelectedIdSousFenetre(idSousFenetre ?? null);
          setShowPopup(true);
          setHasClickedValidate(false);
          setDynamicGraphData([]);
          setAllDataSets([]);
          setCurrentDataSetIndex(0);
        }}
      />

      <div 
        ref={infoBoxRef}
        className={`info-box card p-3 shadow ${infoBoxRetracted ? "retracted" : ""}`}
      >
        <div className="info-box-header d-flex justify-content-between align-items-center mb-3">
          <button
            className={`btn-retract ${infoBoxRetracted ? "" : "blink"}`}
            onClick={() => setInfoBoxRetracted(!infoBoxRetracted)}
            title={infoBoxRetracted ? "Afficher l'info-box" : "Rétrécir"}
          >
            <SafeIcon icon={FaChevronRight} size={20} className={infoBoxRetracted ? "" : "rotate 180"} />
          </button>

          <h4 className="info-title">
            {dynamicGraphData.length > 0 
              ? `Indicateurs : ${selectedSubDomain}` 
              : hasClickedValidate 
                ? "Aucune donnée disponible"
                : "En attente de sélection"}
          </h4>

          <button 
            className="btn-export d-flex align-items-center"
            onClick={exportToPDF}
            disabled={isExporting || (!hasClickedValidate && dynamicGraphData.length === 0)}
          >
            <SafeIcon icon={FaDownload} size={16} className="me-1" />
            <strong>{isExporting ? "Export en cours..." : "Exporter PDF"}</strong>
          </button>
        </div>

        {allDataSets.length > 1 && (
          <div className="d-flex justify-content-between align-items-center mb-3 p-2" style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
            <button 
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentDataSetIndex(prev => Math.max(0, prev - 1))}
              disabled={currentDataSetIndex === 0}
              style={{ minWidth: "40px" }}
            >
              &lt;
            </button>
            <span className="mx-3" style={{ fontSize: "0.9rem", fontWeight: "500" }}>
              Données {currentDataSetIndex + 1} / {allDataSets.length}
            </span>
            <button 
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentDataSetIndex(prev => Math.min(allDataSets.length - 1, prev + 1))}
              disabled={currentDataSetIndex === allDataSets.length - 1}
              style={{ minWidth: "40px" }}
            >
              &gt;
            </button>
          </div>
        )}

        <p className="info-description">
          {dynamicGraphData.length > 0 
            ? "Les informations affichées correspondent au sous-domaine sélectionné."
            : hasClickedValidate
              ? "Aucune donnée n'a été trouvée pour la sélection effectuée."
              : "Sélectionnez un sous-domaine dans le menu et validez une localisation pour afficher les données."}
        </p>

        <div
          ref={exportRef}
          className="stats-container"
        >
          <DynamicCard title="Indicateurs" data={statsData} />
          <p className="text-muted">
            Source : {dynamicGraphData.length > 0 
              ? selectedSubDomain 
              : hasClickedValidate 
                ? "Aucune source"
                : "En attente"}
          </p>
          <DynamicCard title="Répartition" data={pieData} showChart chartType="pie" />
        </div>

        <div className="graph-card">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={graphData}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 8 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 8 }} />
              <Tooltip contentStyle={{ fontSize: 10 }} itemStyle={{ fontSize: 10 }} />
              <Area type="monotone" dataKey="value1" stroke="#2e7d32" strokeWidth={3} fill="#2e7d3222" />
              <Area type="monotone" dataKey="value2" stroke="#1565C0" strokeWidth={3} fill="#1565C022" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {showPopup && (
          <div className="popup-overlay">
            <div className="popup-content card p-3">
              <h5>Sélectionner une localisation</h5>

              <div className="mb-2">
                <label>Ile</label>
                <select
                  className="form-select"
                  value={selectedIle}
                  onChange={(e) => setSelectedIle(Number(e.target.value))}
                >
                  <option value="">Sélectionner</option>
                  {iles.map((i) => (
                    <option key={i.idIle} value={i.idIle}>{i.nomIle}</option>
                  ))}
                </select>
              </div>

              <div className="mb-2">
                <label>Région</label>
                <select
                  className="form-select"
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(Number(e.target.value))}
                >
                  <option value="">Sélectionner</option>
                  {regions.map((r) => (
                    <option key={r.idRegion} value={r.idRegion}>{r.nomRegion}</option>
                  ))}
                </select>
              </div>

              <div className="mb-2">
                <label>Commune</label>
                <select
                  className="form-select"
                  value={selectedCommune}
                  onChange={(e) => setSelectedCommune(Number(e.target.value))}
                >
                  <option value="">Sélectionner</option>
                  {communes.map((c) => (
                    <option key={c.idCommune} value={c.idCommune}>{c.nomCommune}</option>
                  ))}
                </select>
              </div>

              <div className="mb-2">
                <label>Localité</label>
                <select
                  className="form-select"
                  value={selectedLocalite}
                  onChange={(e) => setSelectedLocalite(Number(e.target.value))}
                >
                  <option value="">Sélectionner</option>
                  {localites.map((l) => (
                    <option key={l.idLocalite} value={l.idLocalite}>{l.nomLocalite}</option>
                  ))}
                </select>
              </div>

              {errorMsg && (
                <p style={{ color: 'red', fontSize: '0.75rem', marginTop: '-5px', marginBottom: '5px' }}>
                  {errorMsg}
                </p>
              )}

              <button
                className="btn-primary"
                onClick={() => {
                  if (!selectedIle && !selectedRegion && !selectedCommune && !selectedLocalite) {
                    setErrorMsg("Veuillez sélectionner au moins une option.");
                    return;
                  }
                  setErrorMsg("");
                  setHasClickedValidate(true);
                  setShowPopup(false);
                  (async () => {
                    try {
                      const resultData = await rapportGenereService.getResultDataForGraph(api, {
                        idFenetre: selectedIdFenetre ?? undefined,
                        idSousFenetre: selectedIdSousFenetre ?? undefined,
                        idIle: selectedIle || undefined,
                        idRegion: selectedRegion || undefined,
                        idCommune: selectedCommune || undefined,
                        idLocalite: selectedLocalite || undefined
                      });

                      const result = resultData as any;
                      if (result && result.data && Array.isArray(result.data) && result.data.length > 0) {
                        try {
                          
                          const parsedDataSets: any[][] = [];
                          for (let i = 0; i < result.data.length; i++) {
                            try {
                              const parsed = JSON.parse(result.data[i]);
                              parsedDataSets.push(parsed);
                            } catch (parseError) {
                              console.error(`Erreur parsing dataset ${i + 1}:`, parseError);
                            }
                          }
                          
                          if (parsedDataSets.length > 0) {
                            setAllDataSets(parsedDataSets);
                            setCurrentDataSetIndex(0);
                            setDynamicGraphData(parsedDataSets[0]);
                          } else {
                            setDynamicGraphData([]);
                            setAllDataSets([]);
                          }
                        } catch (parseError) {
                          console.error("Erreur lors du parsing des données :", parseError);
                          setDynamicGraphData([]);
                          setAllDataSets([]);
                        }
                      } else {
                        setDynamicGraphData([]);
                        setAllDataSets([]);
                      }

                    } catch (error) {
                      console.error("Erreur lors de la récupération des données pour le graph :", error);
                      setDynamicGraphData([]);
                    }
                  })();
                }}
              >
                <span style={{margin:"auto"}}>Valider</span>
              </button>
              <CollecteErrorMessage
                message={errorMsg}
                visible={!!errorMsg}
                onClose={() => setErrorMsg("")}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MapPage;